// src/apiServices/authService.js

import axios from 'axios';
import { 
    APIStudentLogout,
    APISupervisorLogout,
    APIInstitutionAdminLogout,
    BASE_URL, 
    APIStudentRegister,
    APISupervisorRegister,
    APIInstitutionAdminRegister,
    APIUserLogin,
    APIUserUpdateProfile,
    APIInstitutionHeadRegister,
} from './APIs';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const USER_INFO_KEY = 'userInfo';
const USER_PERMISSIONS_KEY = 'userPermissions';
const USER_TYPE_KEY = 'user_type';

class AuthService {
    constructor() {
        this.api = axios.create({
            baseURL: BASE_URL,
            timeout: 30000,
            headers: {
                'Accept': 'application/json',
                "Content-Type": "multipart/form-data",
            }
        });

        this.refreshTokenPromise = null;
        this.isRefreshing = false;
        this.failedQueue = [];

        // Request interceptor to add token
        this.api.interceptors.request.use(
            (config) => {
                const token = this.getAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle token refresh
        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Handle 401 errors
                if (error.response?.status === 401 && !originalRequest._retry) {
                    const errorCode = error.response?.data?.code;
                    
                    // Token expired - attempt refresh
                    if (errorCode === 'TOKEN_EXPIRED') {
                        originalRequest._retry = true;
                        
                        try {
                            const newToken = await this.refreshAccessToken();
                            if (newToken) {
                                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                                return this.api(originalRequest);
                            }
                        } catch (refreshError) {
                            this.clearAllData();
                            window.location.href = '/login';
                            return Promise.reject(refreshError);
                        }
                    } 
                    // Invalid token or no token - redirect to login
                    else if (errorCode === 'TOKEN_INVALID' || errorCode === 'TOKEN_NOT_PROVIDED') {
                        this.clearAllData();
                        window.location.href = '/login';
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            }
        );
    }


    /**
     * Get logout endpoint based on user type
     */
    getLogoutEndpoint(userType) {
        switch(userType) {
            case 'student':
                return APIStudentLogout;
            case 'supervisor':
                return APISupervisorLogout;
            case 'university_admin':
                return APIInstitutionAdminLogout;
            default:
                return APIStudentLogout;
        }
    }

    /**
     * Process queue of failed requests
     */
    processQueue(error, token = null) {
        this.failedQueue.forEach(promise => {
            if (error) {
                promise.reject(error);
            } else {
                promise.resolve(token);
            }
        });
        this.failedQueue = [];
    }

    /**
     * Refresh access token using refresh token
     */
    async refreshAccessToken() {
        if (this.isRefreshing) {
            // Wait for the refresh to complete
            return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
            });
        }

        this.isRefreshing = true;
        this.refreshTokenPromise = null;

        try {
            const refreshToken = this.getRefreshToken();
            const userType = this.getUserType();
            
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            // Use user-specific refresh endpoint
            const refreshEndpoint = this.getRefreshEndpoint(userType);
            const response = await axios.post(`${BASE_URL}${refreshEndpoint}`, {
                refresh_token: refreshToken
            });

            if (response.data.status === 'success') {
                const { access_token, refresh_token, expires_in } = response.data;
                
                // Store new tokens
                this.setTokens(access_token, refresh_token, expires_in);
                
                this.isRefreshing = false;
                this.processQueue(null, access_token);
                
                return access_token;
            } else {
                throw new Error('Refresh failed');
            }
        } catch (error) {
            this.isRefreshing = false;
            this.processQueue(error, null);
            this.clearAllData();
            throw error;
        }
    }

    /**
     * Get refresh endpoint based on user type
     */
    getRefreshEndpoint(userType) {
        switch(userType) {
            case 'student':
                return '/api/student/refresh-token';
            case 'supervisor':
                return '/api/supervisor/refresh-token';
            case 'university_admin':
                return '/api/university-admin/refresh-token';
            default:
                return '/api/refresh-token';
        }
    }

    /**
     * Store tokens
     */
    setTokens(accessToken, refreshToken, expiresIn) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        
        // Calculate and store token expiry time
        const expiryTime = Date.now() + (expiresIn * 1000);
        sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    }

    /**
     * Get access token
     */
    getAccessToken() {
        return sessionStorage.getItem(ACCESS_TOKEN_KEY);
    }

    /**
     * Get refresh token
     */
    getRefreshToken() {
        return sessionStorage.getItem(REFRESH_TOKEN_KEY);
    }

    /**
     * Get token expiry
     */
    getTokenExpiry() {
        const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
        return expiry ? parseInt(expiry) : null;
    }

    /**
     * Check if token is expired
     */
    isTokenExpired() {
        const expiry = this.getTokenExpiry();
        if (!expiry) return true;
        return Date.now() >= expiry;
    }

    /**
     * Check if token needs refresh (5 minutes before expiry)
     */
    needsRefresh() {
        const expiry = this.getTokenExpiry();
        if (!expiry) return true;
        const timeUntilExpiry = expiry - Date.now();
        return timeUntilExpiry < 300000; // 5 minutes in milliseconds
    }

    /**
     * Login user
     */
    async userLogin(email, password) {
        try {
            const response = await this.api.post(APIUserLogin, {
                email,
                password
            });

            if (response.data.status === 'success') {
                const { access_token, refresh_token, expires_in, data } = response.data;
                
                // Store tokens
                this.setTokens(access_token, refresh_token, expires_in);
                // Store user info
                sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(data));
                sessionStorage.setItem(USER_PERMISSIONS_KEY, JSON.stringify(data.permissions || ['VIEW']));
                
                // Start auto refresh timer
                // this.startAutoRefreshTimer();

                return {
                    success: true,
                    data: data
                };
            } else {
                return {
                    success: false,
                    message: response.data.message
                };
            }
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed'
            };
        }
    }

    // async getUserProfile() {
    //     try {
    //         const response = await this.api.get(APIUserProfile);
            
    //         if (response.data.status === 'success') {
    //             return {
    //                 success: true,
    //                 data: response.data.data
    //             };
    //         } else {
    //             return {
    //                 success: false,
    //                 message: response.data.message
    //             };
    //         }
    //     } catch (error) {
    //         return {
    //             success: false,
    //             message: error.response?.data?.message || 'Failed to fetch profile'
    //         };
    //     }
    // }

    async updateUserProfile(profileData) {
        try {
            const response = await this.api.post(APIUserUpdateProfile, profileData);
            
            if (response.data.status === 'success') {
                // Update stored user info
                const currentUser = this.getCurrentUser();
                const updatedUser = { ...currentUser, ...response.data.data };
                sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(updatedUser));
                
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message
                };
            } else {
                return {
                    success: false,
                    message: response.data.message
                };
            }
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update profile'
            };
        }
    }



    /**
     * Login user with user type
     */
    async login(endPoint,email, password, userType = 'student') {
        try {
            const response = await this.api.post(endPoint, {email, password});

            if (response.data.status === 'success') {
                const { access_token, refresh_token, expires_in, data } = response.data;
                
                // Store tokens
                // this.setTokens(access_token, refresh_token, expires_in);
                
                // Store user info
                const userInfo = {
                    id: data.id,
                    registration_id: data.registration_id,
                    full_name: data.full_name,
                    email: data.email,
                    contact_phone: data.contact_phone,
                    university: data.university,
                    university_id: data.university_id,
                    supervisor_id:data.supervisor_id,
                    nationality:data.nationality,
                    school_name:data.school_name,
                    school_id:data.school_id,
                    profile_image_path: data.profile_image_path,
                    is_verified: data.is_verified,
                    user_type: userType,
                    ...(data.academic_title && { academic_title: data.academic_title }),
                    ...(data.admin_id_number && { admin_id_number: data.admin_id_number })
                };
                sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
                sessionStorage.setItem(USER_TYPE_KEY, userType);

                // Store permissions if available
                if (data.permissions) {
                    sessionStorage.setItem(USER_PERMISSIONS_KEY, JSON.stringify(data.permissions));
                } else {
                    // Set default permissions based on user type
                    const defaultPermissions = this.getDefaultPermissions(userType);
                    sessionStorage.setItem(USER_PERMISSIONS_KEY, JSON.stringify(defaultPermissions));
                }

                // Start auto refresh timer
                // this.startAutoRefreshTimer();

                return {
                    success: true,
                    data: userInfo,
                    message: response.data.message
                };
            } else {
                return {
                    success: false,
                    message: response.data.message || 'Login failed'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'An error occurred during login'
            };
        }
    }

    /**
     * Get default permissions based on user type
     */
    getDefaultPermissions(userType) {
        switch(userType) {
            case 'student':
                return ['VIEW', 'SUBMIT', 'EDIT_PROFILE'];
            case 'supervisor':
                return ['VIEW', 'REVIEW', 'APPROVE', 'MANAGE_SUPERVISEES', 'EDIT_PROFILE'];
            case 'university_admin':
                return ['VIEW_ALL', 'MANAGE_USERS', 'GENERATE_REPORTS', 'APPROVE', 'EDIT_PROFILE'];
            default:
                return ['VIEW'];
        }
    }

        /**
     * Logout user
     */
    async logoutPlatformUser() {
        try {
            // Clear auto refresh timer
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
            }

            const token = this.getAccessToken();
            const userType = this.getUserType();
            
            if (token && userType) {
                const logoutEndpoint = this.getLogoutEndpoint(userType);
                await this.api.post(logoutEndpoint);
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.clearAllData();
            window.location.href = '/platform-admin/login';
        }
    }

    /**
     * Logout user
     */
    async logout() {
        try {
            // Clear auto refresh timer
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
            }

            const token = this.getAccessToken();
            const userType = this.getUserType();
            
            if (token && userType) {
                const logoutEndpoint = this.getLogoutEndpoint(userType);
                await this.api.post(logoutEndpoint);
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.clearAllData();
            window.location.href = '/login';
        }
    }

    /**
     * Clear all stored data
     */
    clearAllData() {
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        sessionStorage.removeItem(USER_INFO_KEY);
        sessionStorage.removeItem(USER_PERMISSIONS_KEY);
        sessionStorage.removeItem(USER_TYPE_KEY);
    }

    /**
     * Get current user from storage
     */
    getCurrentUser() {
        const userInfo = sessionStorage.getItem(USER_INFO_KEY);
        return userInfo ? JSON.parse(userInfo) : null;
    }

    /**
     * Get user permissions
     */
    getUserPermissions() {
        const permissions = sessionStorage.getItem(USER_PERMISSIONS_KEY);
        return permissions ? JSON.parse(permissions) : ['VIEW'];
    }

    /**
     * Get user type
     */
    getUserType() {
        return sessionStorage.getItem(USER_TYPE_KEY);
    }

    /**
     * Check if user has specific permission
     */
    hasPermission(permission) {
        const permissions = this.getUserPermissions();
        return permissions.includes(permission) || permissions.includes('ALL');
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        const token = this.getAccessToken();
        const userType = this.getUserType();
        return !!(token && userType && !this.isTokenExpired());
    }

    /**
     * Validate current token with server
     */
    async validateToken() {
        try {
            const userType = this.getUserType();
            const validateEndpoint = this.getValidateEndpoint(userType);
            const response = await this.api.post(validateEndpoint);
            return response.data.status === 'success';
        } catch (error) {
            return false;
        }
    }

    /**
     * Get validate endpoint based on user type
     */
    getValidateEndpoint(userType) {
        switch(userType) {
            case 'student':
                return '/api/student/validate-token';
            case 'supervisor':
                return '/api/supervisor/validate-token';
            case 'university_admin':
                return '/api/university-admin/validate-token';
            default:
                return '/api/validate-token';
        }
    }

    /**
     * Auto refresh token if needed
     */
    async autoRefreshToken() {
        if (this.isAuthenticated() && this.needsRefresh()) {
            try {
                await this.refreshAccessToken();
                return true;
            } catch (error) {
                console.error('Auto refresh failed:', error);
                return false;
            }
        }
        return false;
    }

    /**
     * Start auto refresh timer
     */
    startAutoRefreshTimer() {
        // Clear existing timer
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
        // Check every minute
        this.refreshTimer = setInterval(async () => {
            if (this.isAuthenticated() && this.needsRefresh()) {
                await this.autoRefreshToken();
            }
        }, 60000); // 1 minute
    }

    /**
     * Get axios instance
     */
    getApi() {
        return this.api;
    }

    /**
     * Create an authenticated API call with automatic retry
     */
    async authenticatedRequest(config) {
        try {
            const response = await this.api(config);
            return response;
        } catch (error) {
            if (error.response?.status === 401) {
                // Token might be expired, try once more after refresh
                const newToken = await this.refreshAccessToken();
                if (newToken) {
                    config.headers = config.headers || {};
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return this.api(config);
                }
            }
            throw error;
        }
    }

    /**
     * Register methods for different user types
     */
    async registerStudent(data) {
        try {
            const response = await this.api.post(APIStudentRegister, data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    async registerSupervisor(data) {
        try {
            const response = await this.api.post(APISupervisorRegister, data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    async registerUniversityAdmin(data) {
        try {
            const response = await this.api.post(APIInstitutionAdminRegister, data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    async registerInstitutionHead(data) {
        try {
            const response = await this.api.post(APIInstitutionHeadRegister, data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    /**
     * Get dashboard URL based on user type
     */
    getDashboardUrl() {
        const userType = this.getUserType();
        switch(userType) {
            case 'student':
                return '/student/dashboard';
            case 'supervisor':
                return '/supervisor/dashboard';
            case 'institution_admin':
                return '/institution-admin/dashboard';
            default:
                return '/main/dashboard';
        }
    }

    /**
     * Get profile URL based on user type
     */
    getProfileUrl() {
        const userType = this.getUserType();
        switch(userType) {
            case 'student':
                return '/student/profile';
            case 'supervisor':
                return '/supervisor/profile';
            case 'university_admin':
                return '/university-admin/profile';
            default:
                return '/profile';
        }
    }

    /**
     * Update user profile image
     */
    async updateProfileImage(formData) {
        try {
            const userType = this.getUserType();
            const updateEndpoint = this.getUpdateEndpoint(userType);
            const response = await this.api.post(updateEndpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    /**
     * Get update endpoint based on user type
     */
    getUpdateEndpoint(userType) {
        switch(userType) {
            case 'student':
                return '/api/student/update';
            case 'supervisor':
                return '/api/supervisor/update';
            case 'university_admin':
                return '/api/university-admin/update';
            default:
                return '/api/update-profile';
        }
    }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;

